import React, { useEffect, useState } from 'react';
import { Colors } from '@common/tokens';
import { useQuery } from '@common/hooks';
import { BILLING_CARD_ELEGIBLE_ENTITIES_QUERY } from '@institutions/graphql';
import { ErrorHandler, LoadingWrapper, Tabs } from '@common/components';
import { toISODateFormat } from 'works/legacy/lib/date-helper';
import { useRateCardCreationContext } from './_rates-long-term-context';
import { RatesLongTermStepUpdatesTabPendingStaffingRequest } from './_rates-long-term-step-updates-tab-pending-staffing-request';
import { RatesLongTermStepUpdatesTabOpenStaffingRequest } from './_rates-long-term-step-updates-tab-open-staffing-request';
import { RatesLongTermStepUpdatesTabPendingOffer } from './_rates-long-term-step-updates-tab-pending-offer';
import { RatesLongTermStepUpdatesTabExtensionOffer } from './_rates-long-term-step-updates-tab-extension-offer';

export function RatesLongTermStepUpdates() {
  const {
    rateCardAttributes,
    selectedOrganizationUnits,
    setRateCardAttributes,
  } = useRateCardCreationContext();

  const { error, data, loading } = useQuery(
    BILLING_CARD_ELEGIBLE_ENTITIES_QUERY,
    {
      variables: {
        organizationUnitIds: selectedOrganizationUnits.map(({ id }) => id),
        roleId: rateCardAttributes.role?.id || '',
        roleLevel: rateCardAttributes.roleLevel || 'not_applicable',
        effectiveDate: toISODateFormat(rateCardAttributes.effectiveDate),
      },
      skip:
        !rateCardAttributes.role ||
        !rateCardAttributes.roleLevel ||
        !rateCardAttributes.effectiveDate,
    }
  );

  const [activeTab, setActiveTab] = useState('pending-staffing-request');

  const eligibleEntities = data?.billingCardEligibleEntities;
  const pendingStaffingRequestsData = eligibleEntities?.pendingStaffingRequests;
  const openStaffingRequestsData = eligibleEntities?.openStaffingRequests;
  const pendingOffersData = eligibleEntities?.pendingOffers;
  const extensionOffersData = eligibleEntities?.extensionOffers;

  useEffect(() => {
    setRateCardAttributes({
      ...rateCardAttributes,
      pendingStaffingRequestsCount: pendingStaffingRequestsData?.length ?? 0,
      openStaffingRequestsCount: openStaffingRequestsData?.length ?? 0,
      pendingOffersCount: pendingOffersData?.length ?? 0,
      extensionOffersCount: extensionOffersData?.length ?? 0,
    });
    // Page freezes in case of adding rateCardAttributes below due a react infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setRateCardAttributes,
    pendingStaffingRequestsData,
    openStaffingRequestsData,
    pendingOffersData,
    extensionOffersData,
  ]);

  if (error) {
    return <ErrorHandler error={error} log="RatesStepsStepThree" />;
  }

  return (
    <div className="step-three">
      <LoadingWrapper loading={loading}>
        <Tabs activeKey={activeTab} onTabClick={setActiveTab}>
          <Tabs.Pane
            tab={
              <Tabs.Tab
                title="Pending Staffing Request"
                count={pendingStaffingRequestsData?.length ?? 0}
              />
            }
            key="pending-staffing-request"
          >
            <RatesLongTermStepUpdatesTabPendingStaffingRequest
              dataSource={pendingStaffingRequestsData}
            />
          </Tabs.Pane>
          <Tabs.Pane
            tab={
              <Tabs.Tab
                title="Open Staffing Request"
                count={openStaffingRequestsData?.length ?? 0}
              />
            }
            key="open-staffing-request"
          >
            <RatesLongTermStepUpdatesTabOpenStaffingRequest
              dataSource={openStaffingRequestsData}
            />
          </Tabs.Pane>
          <Tabs.Pane
            tab={
              <Tabs.Tab
                title="Pending Offer"
                count={pendingOffersData?.length ?? 0}
              />
            }
            key="pending-offer"
          >
            <RatesLongTermStepUpdatesTabPendingOffer
              dataSource={pendingOffersData}
            />
          </Tabs.Pane>
          <Tabs.Pane
            tab={
              <Tabs.Tab
                title="Extension Offer"
                count={extensionOffersData?.length ?? 0}
              />
            }
            key="extension-offer"
          >
            <RatesLongTermStepUpdatesTabExtensionOffer
              dataSource={extensionOffersData}
            />
          </Tabs.Pane>
        </Tabs>
      </LoadingWrapper>
      <style jsx>
        {`
          :global(.step-three .ant-tabs-nav) {
            width: 100%;
          }

          :global(.step-three .ant-tabs-tab) {
            flex: 1;
          }

          :global(.step-three .ant-tabs-nav > div:nth-of-type(1)) {
            display: flex;
          }

          :global(
            .step-three .ant-table-tbody > tr.ant-table-row-selected > td
          ) {
            background: ${Colors.elmHyperlight};
            border-color: ${Colors.steel};
          }

          :global(
            .step-three .ant-table-selection-column,
            .step-three .ant-table-selection-column .ant-table-selection
          ) {
            text-align: left;
          }
        `}
      </style>
    </div>
  );
}
